// src/data/claimClocks.js
//
// SECTION 539 (2026-09-30; open since ruling 264, section 531 item 6: "appeal and hearing deadlines (the carrier answer
// and the filing window only for now)"). The appeal ladder's own clocks as dates, for the calendar and the Deadlines
// report - and the discipline dates, shared by both.
//
// THE RULE (appealLadder.js, PD-4): a clock is a NUMBER IN AN AGREEMENT. Only the clocks claimRequirements.js carries,
// read verbatim at cited lines (A-8), are used - never a default. The start of each clock is the ACT recorded on the
// claim (appealActs, offProperty.claimActs), and the deadline says so: "from the denial recorded 2026-09-01". Where the
// agreement counts from receipt and the record holds only the act's date, the title says the date is counted from the
// recorded act. Pure.
import { claimActs } from './offProperty';
import { getClaimRequirements } from './claimRequirements';
import { lastDayOfClock } from './timeLimit';   // ruling 314: day one is the day of the event
import { currentSchedule } from './hearingEvents';

const s = (v) => (v == null ? '' : String(v)).trim();
const isDate = (v) => /^\d{4}-\d{2}-\d{2}$/.test(s(v));
const last = (acts, kinds) => acts.filter(a => a && kinds.includes(a.kind) && isDate(a.on)).pop() || null;
const after = (acts, act, kinds) => acts.some(a => a && kinds.includes(a.kind) && isDate(a.on) && a.on >= act.on && a !== act);

// Which clock is which, by the agreement's own label.
function clockFor(req, party, re) {
  const list = (req && Array.isArray(req.clocks)) ? req.clocks : [];
  // A clock stated in months carries no `days` (forensic audit 2026-10-16: the Board clocks added that day) - it counts.
  return list.find(c => c && c.party === party && (Number.isFinite(c.days) || Number.isFinite(c.months)) && re.test(String(c.label || ''))) || null;
}

export function appealDeadlines(claim) {
  const c = claim || {};
  if (!c.status || c.status === 'draft') return [];
  const req = getClaimRequirements(c.applicableAgreement);
  if (!req) return [];
  const acts = claimActs(c);
  const label = s(c.id) || s(c.title) || 'claim';
  const out = [];
  // Forensic audit 2026-10-16: a clock the agreement states in MONTHS (D&H C4/C5, Metra C4, Soo C4, Conrail M/S C6, CSX
  // C5, and the Board clocks added today) is counted in calendar months by timeLimit.lastDayOfMonths and titled in
  // months - it was counted as `days: 183` (2026-01-01 -> 2026-07-02; six months is 2026-06-30) and titled "183 days".
  const per = (clock) => (Number.isFinite(clock.months) ? clock.months + ' months' : clock.days + ' days');
  const push = (date, what, clock) => out.push({ kind: 'deadline', date, title: what + ' - claim ' + label + ' (' + per(clock) + ', ' + (clock.cite || req.claimsRule || 'the agreement') + ')', claimId: c.docId || c.id, source: 'appeal', party: clock.party });   // section 544: whose time it is

  // 1. The Organization's appeal (or listing for conference) after a denial not yet appealed.
  const denial = last(acts, ['carrier_answered', 'conference_decided']);
  const orgAppeal = clockFor(req, 'organization', /appeal|list for discussion|reject/i);
  if (denial && /denied/.test(s(denial.outcome)) && orgAppeal && !after(acts, denial, ['appealed', 'conference_requested', 'board_notice', 'board_submitted'])) {
    push(lastDayOfClock(denial.on, orgAppeal), orgAppeal.label + ' (counted from the denial recorded ' + denial.on + ')', orgAppeal);
  }
  // 2. The Carrier's answer on an appeal not yet answered.
  const appeal = last(acts, ['appealed']);
  const carrierOnAppeal = clockFor(req, 'carrier', /appeal|senior director|after conference/i);
  // Forensic audit 2026-10-16: a carrier clock that runs from the date the claim is DISCUSSED (Amtrak C4, Conrail C4,
  // Conrail M/S C4) is counted from the recorded conference, never from the appeal - it was counted from the appeal.
  const fromDiscussion = !!carrierOnAppeal && /discuss/i.test(s(carrierOnAppeal.from));
  // Forensic audit 2026-10-16 (finding 9): CSX C4 runs "seventy-five (75) calendar days from the date appeal was
  // receipted" (csx-2002-system-agreement.source.txt:1996). Where the clock runs from RECEIPT and the appeal act carries
  // the carrier's receipt date (receivedOn), it is counted from that; without one it is counted from the appeal date -
  // the earliest the receipt can be, so never late - and the title says the receipt is not recorded.
  const fromReceipt = !!carrierOnAppeal && /receipt/i.test(s(carrierOnAppeal.from));
  if (appeal && carrierOnAppeal && !fromDiscussion && !after(acts, appeal, ['carrier_answered', 'conference_decided'])) {
    if (fromReceipt && isDate(appeal.receivedOn)) {
      push(lastDayOfClock(appeal.receivedOn, carrierOnAppeal), carrierOnAppeal.label + " (counted from the carrier's receipt of the appeal recorded " + appeal.receivedOn + ')', carrierOnAppeal);
    } else {
      push(lastDayOfClock(appeal.on, carrierOnAppeal), carrierOnAppeal.label + ' (counted from the appeal recorded ' + appeal.on
        + (fromReceipt ? " - the agreement counts from the carrier's receipt, which is not recorded" : '') + ')', carrierOnAppeal);
    }
  }
  const held = last(acts, ['conference_held']);
  if (fromDiscussion && held && !after(acts, held, ['carrier_answered', 'conference_decided'])) {
    push(lastDayOfClock(held.on, carrierOnAppeal), carrierOnAppeal.label + ' (counted from the conference recorded ' + held.on + ')', carrierOnAppeal);
  }
  // 3. The Board, after a denial on appeal, with no notice to the Board yet.
  // Forensic audit 2026-10-16: Conrail M/S C6 is "Institute subsequent handling" (ConrailMS_modifications_1994.ocr.txt:73)
  // and BN Rule 24 H says "before a tribunal" (BN_YM_Schedule_1997-05-10.source.txt:1126) - neither label says "board",
  // so the Board clock never showed for them. The match now takes the agreements' own words.
  const board = clockFor(req, 'organization', /board|tribunal|subsequent handling/i);
  if (appeal && denial && denial.on >= appeal.on && /denied/.test(s(denial.outcome)) && board && !after(acts, denial, ['board_notice', 'board_submitted'])) {
    push(lastDayOfClock(denial.on, board), board.label + ' (counted from the decision recorded ' + denial.on + ')', board);
  }
  return out;
}

// Discipline: investigation deadlines and scheduled hearings on open cases - one reading for the calendar and reports.
export function disciplineDeadlines(cases) {
  const out = [];
  for (const k of Array.isArray(cases) ? cases : []) {
    if (!k || !k.status || k.status === 'draft' || k.status === 'resolved') continue;
    const who = s(k.employeeName || k.memberName || k.caseNumber || k.id);
    if (isDate(k.investigationDeadline)) out.push({ kind: 'deadline', date: k.investigationDeadline, title: 'Investigation deadline - ' + who, caseId: k.id, source: 'discipline' });
    const sch = currentSchedule(k.hearingEvents, k.hearingLog && k.hearingLog.hearingDate);
    if (sch.state === 'scheduled' && isDate(sch.date)) out.push({ kind: 'deadline', date: sch.date, title: 'Hearing - ' + who, caseId: k.id, source: 'discipline' });
  }
  return out.sort((a, b) => a.date.localeCompare(b.date));
}
